import { useState, useMemo } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import { useTranslation } from 'react-i18next';
import { useGetAuthApi, usePostAuthApi } from '../../hooks/UseApiCalls';
import { LabelDtoIn } from '../../models/in/LabelDtoIn';
import { CreateLabelRequestBody } from '../../models/out/CreateLabelRequestBody';
import LabelChip from './LabelChip';
import { useQueryClient } from '@tanstack/react-query';
import apiClient from '../../api/ApiClient';
import useAuthContext from '../../contexts/Auth/useAuthContext';
import styles from './Pickers.module.scss';
import InquiryAnchoredPopover from './InquiryAnchoredPopover';

/** Default swatch for new labels (matches resource chip / brand in NewInquiryDialog). */
const NEW_LABEL_COLOR = '#3d608a';

interface LabelPickerProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onToggleLabel?: (labelId: number) => void;
  selectedLabelIds: number[];
  inquiryId?: number;
  onChanged?: () => void;
}

export default function LabelPicker({
  anchorEl, onClose, onToggleLabel, selectedLabelIds, inquiryId, onChanged,
}: LabelPickerProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { currentUser } = useAuthContext();
  const authHeaders = { Authorization: `Bearer ${currentUser?.token}` };

  const open = !!anchorEl;
  const { data: labels } = useGetAuthApi<LabelDtoIn[]>(open ? 'management/labels' : '');

  const createLabel = usePostAuthApi<LabelDtoIn, CreateLabelRequestBody>('management/labels', {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['management/labels'] });
      setActionError(null);
    },
    onError: () => setActionError(t('inquiries.labels.createError')),
  });

  const filtered = useMemo(() => {
    if (!search.trim()) return labels || [];
    const s = search.toLowerCase();
    return (labels || []).filter(l => (l.name ?? '').toLowerCase().includes(s));
  }, [labels, search]);

  const showCreateOption = search.trim() &&
    !labels?.some(l => (l.name ?? '').toLowerCase() === search.trim().toLowerCase());

  const handleCreate = () => {
    setActionError(null);
    const body: CreateLabelRequestBody = { name: search.trim(), color: NEW_LABEL_COLOR };
    createLabel.mutate(body);
    setSearch('');
  };

  return (
    <InquiryAnchoredPopover open={open} anchorEl={anchorEl} onClose={onClose} width={320}>
      <div className={styles.pickerSearchWrapper}>
        <span className={styles.pickerSearchIcon}><SearchIcon sx={{ fontSize: 18 }} /></span>
        <input
          className={styles.pickerSearch}
          placeholder={t('inquiries.labels.searchOrCreate')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoFocus
        />
      </div>

      {actionError && (
        <div className={styles.pickerInlineError} role="alert">{actionError}</div>
      )}

      <ul className={styles.pickerList}>
        {filtered.map((label) => {
          const isSelected = selectedLabelIds.includes(label.id);
          return (
            <li key={label.id}>
              <button
                type="button"
                className={isSelected ? styles.pickerItemSelected : styles.pickerItem}
                onClick={async () => {
                  setActionError(null);
                  try {
                    if (onToggleLabel) {
                      onToggleLabel(label.id);
                    } else if (inquiryId) {
                      if (isSelected) {
                        await apiClient.delete(`management/inquiries/${inquiryId}/labels/${label.id}`, { headers: authHeaders });
                      } else {
                        await apiClient.post(`management/inquiries/${inquiryId}/labels`, { labelId: label.id }, { headers: authHeaders });
                      }
                      onChanged?.();
                    }
                  } catch {
                    setActionError(t('inquiries.labels.toggleError'));
                  }
                }}
              >
                <LabelChip name={label.name} color={label.color} />
                <span className={styles.pickerItemTrailing}>
                  <span className={styles.pickerItemSecondary}>{label.inquiryCount}</span>
                  {isSelected && <CheckIcon className={styles.checkIcon} />}
                </span>
              </button>
            </li>
          );
        })}

        {showCreateOption && (
          <>
            <hr className={styles.pickerDivider} />
            <li>
              <button type="button" className={styles.createOption} onClick={handleCreate}>
                <AddIcon sx={{ fontSize: 18 }} />
                {t('inquiries.labels.createNew')} &quot;{search.trim()}&quot;
              </button>
            </li>
          </>
        )}
      </ul>
    </InquiryAnchoredPopover>
  );
}
